// Helpers for ProcessedModelRunHistory rows (see services/processedModelRunHistoryService.ts).

/**
 * The S3 keys a run wrote. `outputS3Keys` is AWSJSON — a JSON-encoded string array written by
 * processedModelExecutorFunction — so it arrives as a string; null / malformed / non-array → [].
 */
export function parseRunOutputKeys(raw: string | string[] | null | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((k): k is string => typeof k === "string");
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
  } catch {
    return [];
  }
}

/** Last path segment of an S3 key, e.g. ".../output/<paramId>/2026-09-16-22.json" → "2026-09-16-22.json". */
export function outputFileName(key: string): string {
  const segments = key.split("/").filter(Boolean);
  return segments[segments.length - 1] || key;
}

/** What a run's status reads as to a user — PROCESSING is "Running", not a pipeline term. */
export const RUN_STATUS_LABEL: Record<string, string> = {
  COMPLETE: "Complete",
  FAILED: "Failed",
  PROCESSING: "Running",
};

/** Pill tone per run status, on the eai-tag-pill-sm frame; matches the delivery calendar's cells. */
export const RUN_STATUS_PILL: Record<string, string> = {
  COMPLETE: "border-eai-success-border bg-eai-success-surface-soft text-eai-success-text",
  FAILED: "border-eai-danger-border bg-eai-danger-surface text-eai-danger-text",
  PROCESSING: "text-eai-info-text",
};

/** A run's wall time, e.g. 850 ms / 12.4 s / 3 min 05 s; null → "—". */
export function formatRunDuration(ms?: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 59_950) return `${(ms / 1000).toFixed(1)} s`;
  // Round to whole seconds first so 119.6 s reads "2 min 00 s", never "1 min 60 s"
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes} min ${String(seconds).padStart(2, "0")} s`;
}

/** A forecast's issue time with its zone — the issue time identifies the result, so it is spelled out. */
export function formatIssuedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "—";
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

/** A run's start as a table cell, e.g. "Oct 14, 10:00 AM" — the year only when it isn't this one. */
export function formatRunStart(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
}
